import { useCallback, useEffect, useRef, useState } from 'react';

export type CameraFacing = 'user' | 'environment';

export function useCameraCapture() {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [facingMode, setFacingMode] = useState<CameraFacing>('environment');
  const [error, setError] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setStream(null);
  }, []);

  const startCamera = useCallback(
    async (facing: CameraFacing = facingMode) => {
      setIsStarting(true);
      setError(null);
      stopStream();
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        streamRef.current = mediaStream;
        setStream(mediaStream);
        setFacingMode(facing);
        return mediaStream;
      } catch {
        setError('Camera access denied');
        throw new Error('Camera access denied');
      } finally {
        setIsStarting(false);
      }
    },
    [facingMode, stopStream]
  );

  const flipCamera = useCallback(async () => {
    const next: CameraFacing = facingMode === 'user' ? 'environment' : 'user';
    await startCamera(next);
  }, [facingMode, startCamera]);

  const capturePhoto = useCallback((): File | null => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return null;

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    if (facingMode === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0);

    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    const binary = atob(dataUrl.split(',')[1]);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new File([bytes], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
  }, [facingMode]);

  useEffect(() => {
    const video = videoRef.current;
    if (video && stream) {
      video.srcObject = stream;
      void video.play().catch(() => {});
    }
  }, [stream]);

  useEffect(() => () => stopStream(), [stopStream]);

  return {
    videoRef,
    stream,
    facingMode,
    error,
    isStarting,
    startCamera,
    flipCamera,
    capturePhoto,
    stopCamera: stopStream,
  };
}
